import { useTranslation } from "react-i18next";
import type { ChangeEvent, HTMLAttributes, Ref } from "react";
import { forwardRef } from "react";
import clsx from "clsx";
import { BounceLoader } from "react-spinners";

import { Icon } from "../atoms/icons";

import classes from "./styles.module.scss";

import type {
  IBasicFormControl,
  IInnerInputProps,
  ILoadingInputIconProps,
} from "@components/formControls/types";
import { Tooltip } from "@/components/atoms/tooltip";
import { p2e } from "@/utils";
import { getDeviceType } from "@/utils/detector";

export const FormInputBox = forwardRef(function FormControlBx(
  props: IBasicFormControl & HTMLAttributes<HTMLDivElement>,
  ref: Ref<HTMLDivElement>,
) {
  const { disabled, readOnly, className, children, validation, ...rest } =
    props;

  return (
    <div
      ref={ref}
      className={clsx([classes.inputBox, className])}
      data-disabled={Boolean(disabled)}
      data-readonly={Boolean(readOnly)}
      data-validation={validation}
      {...rest}
    >
      {children}
    </div>
  );
});

export const InnerInput = forwardRef(function InnerInput(
  props: IInnerInputProps,
  ref: Ref<HTMLInputElement>,
) {
  const { onChange, inputType, className, ...rest } = props;

  const deviceType = getDeviceType();

  function changeHandler(event: ChangeEvent<HTMLInputElement>) {
    const targetValue = p2e(event.target.value);

    if (
      inputType === "customNumber" &&
      Number.isNaN(Number(targetValue.trim()))
    ) {
      return;
    }
    onChange?.(targetValue.trimStart());
  }
  return (
    <input
      className={clsx([classes.input, className])}
      ref={ref}
      onChange={changeHandler}
      type={
        deviceType !== "desktop" && inputType === "customNumber"
          ? "number"
          : inputType
      }
      {...rest}
    />
  );
});

export function InputBGIcon(
  props: HTMLAttributes<HTMLSpanElement> & IBasicFormControl,
) {
  const { children, className, disabled, onMouseDown, readOnly, ...rest } =
    props;

  return (
    <span
      className={clsx([
        classes.bgIcon,
        "d-inline-flex",

        className,
        readOnly || disabled ? "color-inherit" : "color-primary-main",
      ])}
      onMouseDown={(event) => {
        // prevent to focus out input
        event.preventDefault();
        onMouseDown?.(event);
      }}
      {...rest}
    >
      {children}
    </span>
  );
}

export function ActionInputIcon(props: HTMLAttributes<HTMLSpanElement>) {
  const { children, className, onMouseDown, ...rest } = props;

  return (
    <span
      className={clsx([classes.actionIcon, className])}
      onMouseDown={(event) => {
        // prevent to focus out input
        event.preventDefault();
        // prevent to call `FormInputBox` onMouseDownEvent
        event.stopPropagation();
        onMouseDown?.(event);
      }}
      {...rest}
    >
      {children}
    </span>
  );
}

export function InputPrefixSuffix(props: HTMLAttributes<HTMLSpanElement>) {
  const { children, className, onMouseDown, ...rest } = props;

  return (
    <span
      className={clsx([classes.inputPrefix, className])}
      onMouseDown={(event) => {
        event.stopPropagation();
        onMouseDown?.(event);
      }}
      {...rest}
    >
      {children}
    </span>
  );
}

export function LoadingInputIcon(props: ILoadingInputIconProps) {
  const { isError, isLoading, children, errorText } = props;
  const { t } = useTranslation("error");

  if (isError) {
    return (
      <InputBGIcon>
        <Tooltip text={errorText || t("errors.errorOcurred")}>
          <Icon name={"Danger"} color={"danger-main"} />
        </Tooltip>
      </InputBGIcon>
    );
  }
  if (isLoading) {
    return (
      <InputBGIcon>
        <BounceLoader size={18} color={"#29A9E14D"} loading />
      </InputBGIcon>
    );
  }

  return <>{children}</>;
}
